import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AddressInput - Composant de saisie d'adresse avec autocomplétion et géocodage
 * 
 * @param {string} value - Valeur actuelle de l'adresse (texte)
 * @param {function} onChange - Callback appelé avec (addressText, coordinates)
 *   - addressText: string - L'adresse textuelle
 *   - coordinates: {lat, lng} | null - Les coordonnées si disponibles
 * @param {string} placeholder - Placeholder du champ
 */
const AddressInput = ({ value, onChange, placeholder }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const fetchAddressSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/geocoding/search`, {
        params: {
          q: query,
          // No country restriction - allow all ECOWAS/CEDEAO countries
          limit: 8
        }
      });

      if (response.data.success) {
        setSuggestions(response.data.results);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Quand l'utilisateur tape manuellement, pas de coordonnées
    onChange(inputValue, null);

    // Clear previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (inputValue.length >= 2) {
      // Debounce API call by 300ms
      debounceTimeout.current = setTimeout(() => {
        fetchAddressSuggestions(inputValue);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Passer l'adresse ET les coordonnées au parent
    const coordinates = suggestion.lat && suggestion.lon 
      ? { lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) }
      : null;
    
    onChange(suggestion.display_name, coordinates);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 focus:border-[#1db584] focus:ring-[#1db584]"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#1db584] animate-spin" />
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-green-50 text-sm text-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0 flex items-start gap-2"
            >
              <MapPin className="w-4 h-4 text-[#1db584] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{suggestion.address}</div>
                <div className="text-xs text-gray-500 mt-0.5">{suggestion.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressInput;
